import { computed, inject } from '@angular/core';
import { IFileResponse, isPdf } from '@lib/core';
import { MobileService } from '@lib/util';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';

interface IDemandPdfPreviewState {
    readonly pdfFiles: IFileResponse[];
    /** Visibility chosen via the toggle; `null` until the user toggles, then the viewport default applies. */
    readonly displayChoice: boolean | null;
}

const initialState: IDemandPdfPreviewState = {
    pdfFiles: [],
    displayChoice: null,
};

function sameFiles(current: ReadonlyArray<IFileResponse>, next: ReadonlyArray<IFileResponse>): boolean {
    return current.length === next.length && current.every((file, index) => file.id === next[index].id);
}

/**
 * PDF attachments of a demand draft shown next to the form. On the desktop the preview is shown by
 * default; on narrow (mobile) screens it starts hidden so the form stays in view, and is shown above it.
 */
export const DemandPdfPreviewStore = signalStore(
    withState(initialState),
    withProps(() => ({
        _mobileService: inject(MobileService),
    })),
    withComputed(({ pdfFiles, displayChoice, _mobileService }) => {
        const isMobile = computed(() => _mobileService.isMobile().mobile);
        const displayPreview = computed(() => displayChoice() ?? !isMobile());
        return {
            isMobile,
            displayPreview,
            hasPdfFiles: computed(() => pdfFiles().length > 0),
            showPreview: computed(() => displayPreview() && pdfFiles().length > 0),
        };
    }),
    withMethods((store) => ({
        /**
         * Takes over the PDFs among the given attachments. The list is only replaced when the PDFs
         * change, so the preview keeps the selected file across unchanged updates.
         */
        syncFiles(files: ReadonlyArray<IFileResponse>): void {
            const pdfFiles = files.filter((file) => isPdf(file.fileType));
            if (!sameFiles(store.pdfFiles(), pdfFiles)) {
                patchState(store, { pdfFiles });
            }
        },
        togglePreview(): void {
            patchState(store, { displayChoice: !store.displayPreview() });
        },
    })),
);
